import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { antdMessage } from './antdGlobal';
import { ApiResponse } from '../types';

// 将下划线命名转换为驼峰命名
function snakeToCamel(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamel(item));
  }
  
  const result: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelKey] = snakeToCamel(obj[key]);
    }
  }
  return result;
}

// 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { data } = response;
    
    // 业务错误处理
    if (data.code !== 200) {
      antdMessage.error(data.message || '操作失败');
      
      // 401 未授权，清除 token 并跳转登录
      if (data.code === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        window.location.href = '/login';
      }
      
      return Promise.reject(new Error(data.message));
    }
    
    return response;
  },
  (error) => {
    // HTTP 错误处理
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          antdMessage.error('登录已过期，请重新登录');
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          window.location.href = '/login';
          break;
        case 403:
          antdMessage.error('权限不足');
          break;
        case 404:
          antdMessage.error('请求的资源不存在');
          break;
        case 500:
          antdMessage.error('服务器内部错误');
          break;
        default:
          antdMessage.error(data?.message || '请求失败');
      }
    } else if (error.request) {
      antdMessage.error('网络连接失败，请检查网络');
    } else {
      antdMessage.error('请求配置错误');
    }
    
    return Promise.reject(error);
  }
);

// 封装 GET 请求
export function get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<T> {
  return request.get(url, { params, ...config }).then((res) => snakeToCamel(res.data.data));
}

// 封装 POST 请求
export function post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  return request.post(url, data, config).then((res) => snakeToCamel(res.data.data));
}

// 封装 PUT 请求
export function put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
  return request.put(url, data, config).then((res) => snakeToCamel(res.data.data));
}

// 封装 DELETE 请求
export function del<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return request.delete(url, config).then((res) => snakeToCamel(res.data.data));
}

export default request;
